// Route-aware PWA manifest selection (Session AFG). The app ships TWO web app manifests on one
// origin: the office manifest (vite-plugin-pwa generates /manifest.webmanifest from pwaManifest.ts,
// start_url "/", installs as "ELE-Guvnor") and a separate engineer manifest (public/engineer-
// manifest.json, start_url + scope "/engineer/", installs as "ELE Engineer"). A browser installs
// whichever manifest is linked by <link rel="manifest"> at the moment the user taps "Add to Home
// Screen", so the linked manifest must reflect the CURRENT surface: an engineer installing from an
// /engineer page must get the engineer-scoped app, not the office app.
//
// Why this matters: with a single office manifest (start_url "/"), a freshly-installed engineer app
// opened at "/" bounces through RootRedirect to the OFFICE Google sign-in whenever the installed
// PWA's storage jar has no session, so the engineer sees the office interface. The engineer manifest
// opens the installed app at /engineer (the engineer sign-in), locked to the /engineer/ scope.
//
// The vite-injected office <link> IS the default; this only swaps its href to the engineer manifest
// on an /engineer surface and restores the office href elsewhere. In dev the SW/manifest is disabled
// so no <link> exists and every call is a safe no-op. isEngineerPath is pure + exported for tests.

export const ENGINEER_MANIFEST_HREF = "/engineer-manifest.json";
const DEFAULT_OFFICE_MANIFEST_HREF = "/manifest.webmanifest";
// Where the office manifest href is stashed, ON the link element (per-document, no module-global
// state), so restoring it never hardcodes the vite-generated filename.
const OFFICE_HREF_ATTR = "data-office-manifest-href";

// True for the engineer PWA surface only: /engineer exactly, or any /engineer/ sub-path. A path that
// merely starts with the letters "engineer" (there is none today, but be defensive) does not match.
export function isEngineerPath(pathname: string): boolean {
  return pathname === "/engineer" || pathname.startsWith("/engineer/");
}

// Point <link rel="manifest"> at the manifest that matches the given path. Idempotent (only writes
// when the href actually changes) and a no-op when no manifest link is present (dev, or a document
// without one). The document is injectable so the swap logic is unit-testable.
export function syncManifestLink(pathname: string, doc: Document = document): void {
  const link = doc.querySelector('link[rel="manifest"]');
  if (!link) return;
  // First run for this link: the current href IS the office manifest (the build injects it). Stash
  // it so a later restore uses the real generated filename, not a hardcoded guess.
  let officeHref = link.getAttribute(OFFICE_HREF_ATTR);
  if (officeHref === null) {
    officeHref = link.getAttribute("href") ?? DEFAULT_OFFICE_MANIFEST_HREF;
    link.setAttribute(OFFICE_HREF_ATTR, officeHref);
  }
  const desired = isEngineerPath(pathname) ? ENGINEER_MANIFEST_HREF : officeHref;
  if (link.getAttribute("href") !== desired) link.setAttribute("href", desired);
}
